import React from "react";
import { usePulseAnimation } from "../hooks/usePulseAnimation";
import { Tooltip } from "./Tooltip";

interface CircularButtonProps {
  onClick: () => void;
  disabled?: boolean;
  size?: number; // width/height in px
  ariaLabel?: string;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
  tooltip?: React.ReactNode;
  tooltipShortcut?: string;
  tooltipPosition?: "top" | "bottom" | "left" | "right";
  tooltipDelay?: number;
}

export const CircularButton: React.FC<CircularButtonProps> = ({
  onClick,
  disabled = false,
  size = 48,
  ariaLabel,
  className,
  style,
  children,
  tooltip,
  tooltipShortcut,
  tooltipPosition = "top",
  tooltipDelay = 500,
}) => {
  const { pulseState, scaleState, triggerPulse } = usePulseAnimation();

  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Only apply hover effects on devices that actually support hover
    if (
      !disabled &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches
    ) {
      e.currentTarget.style.background = "var(--button-hover-bg)";
      e.currentTarget.style.boxShadow = "0 12px 40px rgba(0, 0, 0, 0.2)";
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Only apply hover effects on devices that actually support hover
    if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
      e.currentTarget.style.background = disabled
        ? "rgba(75, 85, 99, 0.2)"
        : "var(--circular-button-bg)";
      e.currentTarget.style.boxShadow = disabled
        ? "none"
        : "0 8px 32px rgba(0, 0, 0, 0.1)";
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    triggerPulse(e);
    onClick();
  };

  const buttonElement = (
    <button
      aria-label={ariaLabel}
      onClick={handleClick}
      disabled={disabled}
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: "50%",
        border: "none",
        background: disabled
          ? "rgba(75, 85, 99, 0.2)"
          : "var(--circular-button-bg)",
        color: disabled ? "var(--text-muted)" : "var(--text-primary)",
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition:
          "background 150ms ease, box-shadow 150ms ease, transform 0.1s ease",
        transform: `scale(${scaleState.scale})`,
        backdropFilter: "blur(7px)",
        boxShadow: disabled ? "none" : "0 8px 32px rgba(0, 0, 0, 0.1)",
        position: "relative",
        overflow: "hidden",
        opacity: disabled ? 0.6 : 1,
        ...style,
      }}
    >
      {pulseState.isActive && (
        <div
          style={{
            position: "absolute",
            left: pulseState.x,
            top: pulseState.y,
            width: 0,
            height: 0,
            borderRadius: "50%",
            background: "var(--button-hover-bg)",
            transform: "translate(-50%, -50%)",
            animation: "pulseExpand 100ms ease-out forwards",
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
      )}
      {children}
    </button>
  );

  return tooltip ? (
    <Tooltip
      text={tooltip}
      position={tooltipPosition}
      delay={tooltipDelay}
      shortcut={tooltipShortcut}
    >
      {buttonElement}
    </Tooltip>
  ) : (
    buttonElement
  );
};
